import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  getMasters, createMaster, updateMaster, setMasterActive,
} from '../../services/api';
import { formatDate } from '../../utils/formatDateTime';

/**
 * Gestão de usuários master do sistema.
 *
 * Regras:
 *   - Qualquer master ativo pode criar, editar e ativar/desativar outros masters.
 *   - NINGUÉM pode desativar a si mesmo (botão "Desativar" escondido no próprio card).
 *   - Ao criar, o sistema gera senha temporária e envia e-mail de boas-vindas
 *     com o padrão default da AtendoApp (sem branding de tenant).
 *   - E-mail NÃO é editável depois de criado — só nome. Mudar e-mail exige
 *     desativar o antigo e criar um novo.
 */
export default function MasterAdmins() {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const selfId = currentUser?.id;

  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modal, setModal] = useState(null); // null | 'new' | { ...edit }
  const [form, setForm] = useState({ name: '', email: '' });
  const [saving, setSaving] = useState(false);

  const [acting, setActing] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getMasters()
      .then(r => setMasters(r.data.masters || []))
      .catch(() => toast.error('Erro ao carregar usuários master'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setModal('new');
    setForm({ name: '', email: '' });
  };

  const openEdit = (m) => {
    setModal(m);
    setForm({ name: m.name, email: m.email });
  };

  const closeModal = () => {
    if (saving) return;
    setModal(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      return toast.error('Informe um nome válido');
    }
    if (modal === 'new') {
      if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
        return toast.error('Informe um e-mail válido');
      }
    }

    setSaving(true);
    try {
      if (modal === 'new') {
        const r = await createMaster({ name: form.name.trim(), email: form.email.trim().toLowerCase() });
        const temp = r.data?.temp_password;
        toast.success(
          temp
            ? `Master criado! Senha temporária: ${temp}`
            : 'Master criado! Um e-mail de boas-vindas foi enviado.',
          { duration: 8000 }
        );
      } else {
        await updateMaster(modal.id, { name: form.name.trim() });
        toast.success('Master atualizado!');
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    if (m.id === selfId) {
      toast.error('Você não pode desativar seu próprio usuário master.');
      return;
    }
    const nextActive = !m.active;
    const ok = await confirm({
      title: nextActive ? `Reativar "${m.name}"?` : `Desativar "${m.name}"?`,
      message: nextActive
        ? 'Este master voltará a ter acesso ao painel imediatamente.'
        : 'Este master não poderá mais acessar o painel até ser reativado. Sessões ativas serão mantidas até expirarem.',
      confirmText: nextActive ? 'Reativar' : 'Desativar',
      variant: nextActive ? 'success' : 'warning',
    });
    if (!ok) return;

    setActing(p => ({ ...p, [m.id]: true }));
    try {
      await setMasterActive(m.id, nextActive);
      toast.success(nextActive ? 'Master reativado.' : 'Master desativado.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao alterar status');
    } finally {
      setActing(p => ({ ...p, [m.id]: false }));
    }
  };

  const activeCount  = masters.filter(m => m.active).length;
  const inactiveCount = masters.length - activeCount;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Usuários Master</h2>
          <p>{activeCount} ativo(s){inactiveCount > 0 ? ` · ${inactiveCount} inativo(s)` : ''}</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openNew}>+ Novo Master</button>
        </div>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
      ) : masters.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👤</div>
          <h3>Nenhum usuário master cadastrado</h3>
          <p>Clique em "+ Novo Master" para adicionar o primeiro.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 16, marginTop: 8 }}>
          {masters.map(m => {
            const isSelf = m.id === selfId;
            return (
              <div key={m.id} className="mobile-card" style={{
                background: 'var(--white)',
                borderRadius: 12,
                border: '1px solid var(--gray-200)',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                opacity: m.active ? 1 : 0.65,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 22,
                    background: 'var(--primary)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 800, fontSize: 18, flexShrink: 0,
                  }}>
                    {(m.name || 'M')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {m.name}
                      {isSelf && (
                        <span className="badge badge-info" style={{ fontSize: 10 }}>Você</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.email}
                    </div>
                  </div>
                  <span className={`badge ${m.active ? 'badge-success' : 'badge-gray'}`}>
                    {m.active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>

                {m.must_change_password ? (
                  <div className="info-box" style={{ fontSize: 12, padding: '6px 10px' }}>
                    🔑 Precisa trocar a senha no primeiro acesso
                  </div>
                ) : null}

                <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                  Criado em {formatDate(m.created_at)}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4, borderTop: '1px solid var(--gray-100)' }}>
                  <button
                    className="btn btn-xs btn-secondary"
                    style={{ flex: 1, minWidth: 80 }}
                    onClick={() => openEdit(m)}
                    disabled={acting[m.id]}
                  >
                    Editar
                  </button>
                  {!isSelf && (
                    <button
                      className={`btn btn-xs ${m.active ? 'btn-warning' : 'btn-success'}${acting[m.id] ? ' btn-loading' : ''}`}
                      style={{ flex: 1, minWidth: 80 }}
                      onClick={() => toggleActive(m)}
                      disabled={acting[m.id]}
                    >
                      {acting[m.id] ? '' : m.active ? 'Desativar' : 'Reativar'}
                    </button>
                  )}
                  {isSelf && (
                    <div style={{
                      flex: 1, minWidth: 80,
                      fontSize: 11, color: 'var(--gray-400)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontStyle: 'italic', textAlign: 'center',
                    }}>
                      Não pode desativar a si mesmo
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal novo/editar */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3>{modal === 'new' ? 'Novo usuário master' : `Editar ${modal.name}`}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    autoFocus
                    placeholder="Nome do usuário master"
                    disabled={saving}
                  />
                </div>
                <div className="form-group">
                  <label>E-mail {modal === 'new' ? '*' : ''}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => modal === 'new' && setForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="usuario@atendoapp.com"
                    disabled={saving || modal !== 'new'}
                    readOnly={modal !== 'new'}
                  />
                  {modal !== 'new' && (
                    <small>O e-mail não pode ser alterado. Para trocar, desative este master e crie um novo.</small>
                  )}
                </div>
                {modal === 'new' && (
                  <div className="info-box" style={{ fontSize: 12, marginTop: 8 }}>
                    💡 Uma senha temporária será gerada automaticamente e enviada por e-mail.
                    O novo master será obrigado a trocá-la no primeiro acesso.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={saving}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>
                  {saving ? 'Salvando...' : (modal === 'new' ? 'Criar master' : 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
